import React, { useState, useEffect, useRef } from 'react';
import { firestore } from '../firebase/config';
import { collection, addDoc, onSnapshot, query, orderBy, where, doc, updateDoc, getDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { useToast } from './ToastContext';

const ChatIcon = ({ userRole }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all'); // 'all', 'students', 'employees'
  const [recipientId, setRecipientId] = useState('');
  const [recipients, setRecipients] = useState({ students: [], employees: [], interns: [], managers: [] });
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [previousMessagesCount, setPreviousMessagesCount] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null); // Track message being replied to
  const messagesEndRef = useRef(null);
  const searchRef = useRef(null);
  const { showSuccess, showError } = useToast();

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Periodic cleanup of old messages (every hour)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (messages.length > 0) {
        deleteOldMessages(messages);
      }
    }, 60 * 60 * 1000); // Run every hour

    return () => clearInterval(cleanupInterval);
  }, [messages]);

  // Function to send browser notification
  const sendNotification = (title, body, icon = null) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'chat-notification',
        requireInteraction: false,
        silent: false
      });
    }
  };

  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio notification not supported');
    }
  };

  // Function to check if message is older than 48 hours
  const isMessageOlderThan48Hours = (timestamp) => {
    const messageTime = new Date(timestamp);
    const currentTime = new Date();
    const timeDifference = currentTime.getTime() - messageTime.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference > 48;
  };

  // Function to delete old messages from Firebase
  const deleteOldMessages = async (messages) => {
    const oldMessages = messages.filter(msg => isMessageOlderThan48Hours(msg.timestamp));
    
    if (oldMessages.length > 0) {
      try {
        const deletePromises = oldMessages.map(msg => 
          deleteDoc(doc(firestore, 'messages', msg.id))
        );
        await Promise.all(deletePromises);
        console.log(`Deleted ${oldMessages.length} old messages`);
      } catch (error) {
        console.error('Error deleting old messages:', error);
      }
    }
  };

     // Fetch recipients (students, employees, interns, managers)
   useEffect(() => {
     const fetchRecipients = async () => {
       try {
         // Fetch students
         const studentsSnapshot = await getDocs(collection(firestore, 'students'));
         const students = studentsSnapshot.docs.map(doc => ({
           id: doc.id,
           name: doc.data().fullName,
           type: 'student'
         }));

         // Fetch employees
         const employeesSnapshot = await getDocs(collection(firestore, 'employees'));
         const employees = employeesSnapshot.docs.map(doc => ({
           id: doc.id,
           name: doc.data().fullName,
           type: 'employee'
         }));

         // Fetch interns
         const internsSnapshot = await getDocs(collection(firestore, 'interns'));
         const interns = internsSnapshot.docs.map(doc => ({
           id: doc.id,
           name: doc.data().fullName,
           type: 'intern'
         }));

         // Fetch managers
         const managersSnapshot = await getDocs(collection(firestore, 'managers'));
         const managers = managersSnapshot.docs.map(doc => ({
           id: doc.id,
           name: doc.data().fullName,
           type: 'manager'
         }));

         setRecipients({ students, employees, interns, managers });
       } catch (error) {
         console.error('Error fetching recipients:', error);
       }
     };

     fetchRecipients();
   }, []);

  // Listen for new messages
  useEffect(() => {
    if (!isOpen) return;

    const messagesQuery = query(
      collection(firestore, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter out old messages (older than 48 hours)
      const filteredMessages = newMessages.filter(msg => !isMessageOlderThan48Hours(msg.timestamp));

      // Check for new messages and send notifications
      if (filteredMessages.length > previousMessagesCount && previousMessagesCount > 0) {
        const newIncomingMessages = filteredMessages.slice(previousMessagesCount);
        newIncomingMessages.forEach(message => {
          // Only notify for messages from non-admin users
          if (message.senderType !== 'admin' && !message.read) {
            const senderName = message.senderName || 
              (message.senderType === 'student' ? 'Student' :
               message.senderType === 'employee' ? 'Employee' :
               message.senderType === 'intern' ? 'Intern' :
               message.senderType === 'manager' ? 'Manager' : 'User');
            
            const notificationTitle = `New message from ${senderName}`;
            const notificationBody = message.text.length > 50 
              ? `${message.text.substring(0, 50)}...` 
              : message.text;
            
            // Send browser notification
            sendNotification(notificationTitle, notificationBody);
            
            // Play notification sound
            playNotificationSound();
            
            // Send toast notification if chat is not open
            if (!isOpen) {
              showSuccess(`New message from ${senderName}`);
            }
          }
        });
      }

      setMessages(filteredMessages);
      setPreviousMessagesCount(filteredMessages.length);
      
      // Delete old messages from Firebase (run periodically)
      deleteOldMessages(newMessages);
      
      // Count unread messages (messages from non-admin users)
      const unread = filteredMessages.filter(msg => !msg.read && msg.senderType !== 'admin').length;
      setUnreadCount(unread);
    });

    return () => unsubscribe();
  }, [isOpen]);

  // Auto scroll to bottom when new messages arrive or when chat is opened
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, isOpen]);

  // Mark all unread messages as read when chat is opened
  const markAllAsRead = async () => {
    const unreadMessages = messages.filter(msg => !msg.read && msg.senderType !== 'admin');
    const updatePromises = unreadMessages.map(msg => 
      updateDoc(doc(firestore, 'messages', msg.id), { read: true })
    );
    
    try {
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Mark all messages as read when chat is opened and scroll to first unread message
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      markAllAsRead();
      
      // Find first unread message and scroll to it
      const firstUnreadIndex = messages.findIndex(msg => !msg.read && msg.senderType !== 'admin');
      if (firstUnreadIndex !== -1) {
        setTimeout(() => {
          const messageElements = document.querySelectorAll('[data-message-id]');
          if (messageElements[firstUnreadIndex]) {
            messageElements[firstUnreadIndex].scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 200);
      }
    }
  }, [isOpen, messages, unreadCount]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      const messageData = {
        text: newMessage.trim(),
        senderType: 'admin',
        senderId: 'admin',
        senderName: 'Admin',
        timestamp: new Date().toISOString(),
        read: false,
        recipientType,
        recipientId: recipientId || null,
        isBroadcast: recipientType === 'all',
        replyTo: replyingTo ? {
          messageId: replyingTo.id,
          text: replyingTo.text,
          senderName: replyingTo.senderName,
          senderType: replyingTo.senderType
        } : null
      };

      await addDoc(collection(firestore, 'messages'), messageData);
      setNewMessage('');
      setReplyingTo(null); // Clear reply state
      showSuccess('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message');
    }
    setLoading(false);
  };

  const markAsRead = async (messageId) => {
    try {
      await updateDoc(doc(firestore, 'messages', messageId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleMessageDoubleClick = (message) => {
    setReplyingTo(message);
    // Focus on the message input
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder*="Type your message"]');
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

     const getRecipientName = (message) => {
     if (message.isBroadcast) {
       return message.recipientType === 'all' ? 'All Users' : 
              message.recipientType === 'students' ? 'All Students' : 
              message.recipientType === 'employees' ? 'All Employees' :
              message.recipientType === 'interns' ? 'All Interns' :
              message.recipientType === 'managers' ? 'All Managers' : 'All Users';
     }
     
     if (message.recipientId) {
       const allRecipients = [...recipients.students, ...recipients.employees, ...recipients.interns, ...recipients.managers];
       const recipient = allRecipients.find(r => r.id === message.recipientId);
       return recipient ? recipient.name : 'Unknown';
     }
     
     return 'Unknown';
   };

     // Filter recipients based on search term
   const filteredRecipients = () => {
     if (!searchTerm.trim()) {
       return {
         students: recipients.students,
         employees: recipients.employees,
         interns: recipients.interns,
         managers: recipients.managers
       };
     }

     const searchLower = searchTerm.toLowerCase();
     return {
       students: recipients.students.filter(student => 
         student.name.toLowerCase().includes(searchLower)
       ),
       employees: recipients.employees.filter(employee => 
         employee.name.toLowerCase().includes(searchLower)
       ),
       interns: recipients.interns.filter(intern => 
         intern.name.toLowerCase().includes(searchLower)
       ),
       managers: recipients.managers.filter(manager => 
         manager.name.toLowerCase().includes(searchLower)
       )
     };
   };

  const handleRecipientSelect = (recipientId, recipientName) => {
    setRecipientId(recipientId);
    setSearchTerm(recipientName);
    setShowSuggestions(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    if (!e.target.value.trim()) {
      setRecipientId('');
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
             {/* Fixed Chat Icon */}
       <div className="fixed bottom-6 right-6 z-50">
         <button
           onClick={() => setIsOpen(!isOpen)}
           className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-indigo-300 backdrop-blur-sm"
           title="Admin Chat - Send messages to students and employees"
         >
           <div className="absolute inset-0 bg-white/10 rounded-full animate-pulse"></div>
           <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
           </svg>
           {unreadCount > 0 && !isOpen && (
             <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-bounce shadow-lg">
               {unreadCount > 9 ? '9+' : unreadCount}
             </span>
           )}
         </button>
       </div>

             {/* Chat Modal */}
       {isOpen && (
         <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center animate-in slide-in-from-bottom-4 duration-300">
                       <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-2xl w-[90%] h-full flex flex-col border border-gray-200/50">
                         {/* Header */}
             <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white p-6 relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
               <div className="relative z-10 flex items-center justify-between">
                 <div className="flex items-center space-x-4">
                   <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                     </svg>
                   </div>
                   <div>
                                           <h3 className="font-bold text-xl flex items-center space-x-3">
                        <span>Admin Chat</span>
                        {isOpen && unreadCount > 0 && (
                          <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-pulse shadow-lg">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                        {/* Special indicator for admin-specific messages */}
                        {isOpen && messages.some(msg => msg.recipientId === 'admin' && msg.senderType !== 'admin') && (
                          <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs rounded-full px-3 py-1 flex items-center space-x-1 font-bold animate-pulse shadow-lg">
                            <span>👑</span>
                            <span>Direct Messages</span>
                          </span>
                        )}
                      </h3>
                     <p className="text-indigo-100 text-sm font-medium">Send messages to students & employees</p>
                   </div>
                 </div>
                 <button
                   onClick={() => setIsOpen(false)}
                   className="text-white hover:text-indigo-100 transition-all duration-200 hover:scale-110 p-2 rounded-full hover:bg-white/10"
                 >
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
               </div>
             </div>

            {/* Recipient Filter Buttons */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setRecipientType('all');
                    setRecipientId('');
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                    recipientType === 'all' && !recipientId
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  👥 All Users
                </button>
                <button
                  onClick={() => {
                    setRecipientType('students');
                    setRecipientId('');
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                    recipientType === 'students' && !recipientId
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  🎓 All Students
                </button>
                <button
                  onClick={() => {
                    setRecipientType('employees');
                    setRecipientId('');
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                    recipientType === 'employees' && !recipientId
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  👨‍💼 All Employees
                </button>
                <button
                  onClick={() => {
                    setRecipientType('interns');
                    setRecipientId('');
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                    recipientType === 'interns' && !recipientId
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  🎯 All Interns
                </button>
                <button
                  onClick={() => {
                    setRecipientType('managers');
                    setRecipientId('');
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                    recipientType === 'managers' && !recipientId
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  👑 All Managers
                </button>
                <button
                  onClick={() => {
                    setRecipientType('specific');
                    setRecipientId('');
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                    recipientType === 'specific'
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  👤 Specific Person
                </button>
                </div>

                {/* Specific Recipient Selection */}
                {recipientType === 'specific' && (
                <div className="mt-3 relative" ref={searchRef}>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="Search for recipient..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    
                    {/* Suggestions Dropdown */}
                    {showSuggestions && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {(() => {
                          const filtered = filteredRecipients();
                                                     const hasResults = filtered.students.length > 0 || filtered.employees.length > 0 || filtered.interns.length > 0 || filtered.managers.length > 0;
                          
                          if (!hasResults && searchTerm.trim()) {
                            return (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No recipients found for "{searchTerm}"
                              </div>
                            );
                          }
                          
                          return (
                            <>
                              {filtered.students.length > 0 && (
                                <div className="px-2 py-1">
                                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">Students</div>
                                  {filtered.students.map(student => (
                                    <button
                                      key={`student-${student.id}`}
                                      onClick={() => handleRecipientSelect(student.id, student.name)}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 rounded-md flex items-center space-x-2"
                                    >
                                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                      <span>{student.name}</span>
                                      <span className="text-xs text-gray-400">(Student)</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              
                                                             {filtered.employees.length > 0 && (
                                 <div className="px-2 py-1">
                                   <div className="text-xs font-semibold text-gray-500 px-2 py-1">Employees</div>
                                   {filtered.employees.map(employee => (
                                     <button
                                       key={`employee-${employee.id}`}
                                       onClick={() => handleRecipientSelect(employee.id, employee.name)}
                                       className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 rounded-md flex items-center space-x-2"
                                     >
                                       <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                       <span>{employee.name}</span>
                                       <span className="text-xs text-gray-400">(Employee)</span>
                                     </button>
                                   ))}
                                 </div>
                               )}
                               
                               {filtered.interns.length > 0 && (
                                 <div className="px-2 py-1">
                                   <div className="text-xs font-semibold text-gray-500 px-2 py-1">Interns</div>
                                   {filtered.interns.map(intern => (
                                     <button
                                       key={`intern-${intern.id}`}
                                       onClick={() => handleRecipientSelect(intern.id, intern.name)}
                                       className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 rounded-md flex items-center space-x-2"
                                     >
                                       <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                       <span>{intern.name}</span>
                                       <span className="text-xs text-gray-400">(Intern)</span>
                                     </button>
                                   ))}
                                 </div>
                               )}
                               
                               {filtered.managers.length > 0 && (
                                 <div className="px-2 py-1">
                                   <div className="text-xs font-semibold text-gray-500 px-2 py-1">Managers</div>
                                   {filtered.managers.map(manager => (
                                     <button
                                       key={`manager-${manager.id}`}
                                       onClick={() => handleRecipientSelect(manager.id, manager.name)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 rounded-md flex items-center space-x-2"
                                     >
                                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                                       <span>{manager.name}</span>
                                       <span className="text-xs text-gray-400">(Manager)</span>
                                     </button>
                                   ))}
                                 </div>
                               )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                </div>
              )}

              {/* Selected Recipient Display */}
              {(recipientId || (recipientType !== 'all' && recipientType !== 'specific')) && (
                <div className="mt-3 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-blue-600">
                      📤 Sending to: {recipientId 
                        ? (recipients.students.find(r => r.id === recipientId)?.name || 
                           recipients.employees.find(r => r.id === recipientId)?.name ||
                           recipients.interns.find(r => r.id === recipientId)?.name ||
                           recipients.managers.find(r => r.id === recipientId)?.name || 'Unknown')
                        : recipientType === 'students' ? 'All Students'
                        : recipientType === 'employees' ? 'All Employees'
                        : recipientType === 'interns' ? 'All Interns'
                        : recipientType === 'managers' ? 'All Managers'
                        : 'All Users'
                      }
                    </span>
                    <button
                      onClick={() => {
                        setRecipientId('');
                        setRecipientType('all');
                      }}
                      className="text-blue-500 hover:text-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Message Input Section */}
            <div className="p-4 border-b border-gray-200">
              <div className="space-y-3">
                {/* Reply Preview */}
                {replyingTo && (
                  <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-blue-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        Replying to {replyingTo.senderName || 'Unknown'}
                      </span>
                      <button
                        onClick={cancelReply}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="text-sm text-gray-700 bg-white/50 rounded-lg p-2 border-l-4 border-blue-400">
                      {replyingTo.text.length > 100 
                        ? `${replyingTo.text.substring(0, 100)}...` 
                        : replyingTo.text
                      }
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="flex space-x-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none min-h-[60px] max-h-[120px]"
                    rows="2"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={loading || !newMessage.trim()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>No messages yet</p>
                  <p className="text-sm">Start a conversation!</p>
                </div>
              ) : (
                messages.map((message) => (
                                     <div
                     key={message.id}
                     data-message-id={message.id}
                     className={`flex ${message.senderType === 'admin' ? 'justify-end' : 'justify-start'} cursor-pointer hover:opacity-80 transition-opacity`}
                     onClick={() => !message.read && message.senderType !== 'admin' && markAsRead(message.id)}
                     onDoubleClick={() => handleMessageDoubleClick(message)}
                     title="Double-click to reply"
                   >
                                         <div
                       className={`w-[60%] px-4 py-3 rounded-2xl shadow-lg ${
                         message.senderType === 'admin'
                           ? 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-purple-500/25'
                           : message.recipientId === 'admin' && message.senderType !== 'admin'
                           ? 'bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 text-gray-800 border-2 border-yellow-300 shadow-yellow-500/25'
                           : 'bg-gradient-to-br from-white via-gray-50 to-gray-100 text-gray-800 border border-gray-200/50 shadow-gray-500/10'
                       } ${!message.read && message.senderType !== 'admin' ? 'ring-2 ring-purple-300 shadow-xl' : ''} ${message.recipientId === 'admin' && message.senderType !== 'admin' ? 'ring-2 ring-yellow-400 shadow-yellow-500/50' : ''} backdrop-blur-sm relative`}
                     >
                       {/* Reply Preview */}
                       {message.replyTo && (
                         <div className="mb-3 p-2 bg-black/10 rounded-lg border-l-4 border-white/30">
                           <div className="text-xs opacity-80 mb-1 flex items-center">
                             <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                             </svg>
                             {message.replyTo.senderName || 'Unknown'}
                           </div>
                           <div className="text-xs opacity-70">
                             {message.replyTo.text.length > 50 
                               ? `${message.replyTo.text.substring(0, 50)}...` 
                               : message.replyTo.text
                             }
                           </div>
                         </div>
                       )}

                                             <div className="flex items-center space-x-2 mb-2">
                         <span className="text-xs font-semibold flex items-center">
                           <span className="w-2 h-2 rounded-full mr-2 ${
                             message.senderType === 'admin' ? 'bg-purple-400' :
                             message.senderType === 'student' ? 'bg-emerald-400' :
                             message.senderType === 'employee' ? 'bg-purple-400' :
                             message.senderType === 'intern' ? 'bg-orange-400' :
                             message.senderType === 'manager' ? 'bg-indigo-400' : 'bg-gray-400'
                           }"></span>
                           {message.senderType === 'admin' ? 'You' : message.senderName || 'Unknown'}
                         </span>
                         {message.senderType !== 'admin' && !message.read && (
                           <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse shadow-sm"></span>
                         )}
                         {/* Special crown icon for admin-specific messages */}
                         {message.recipientId === 'admin' && message.senderType !== 'admin' && (
                           <span className="text-yellow-500 text-lg animate-pulse">👑</span>
                         )}
                       </div>
                                             <div className="text-sm break-words whitespace-pre-wrap overflow-hidden word-wrap leading-relaxed font-medium">
                         {message.text}
                       </div>
                                                                    <div className="text-xs opacity-70 mt-2 flex items-center space-x-1">
                         <span className="flex items-center">
                           <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                           </svg>
                           {formatTime(message.timestamp)}
                         </span>
                       </div>
                       {message.senderType === 'admin' && (
                         <div className="text-xs opacity-70 mt-2 flex items-center space-x-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                           <span>📤</span>
                           <span>To: {getRecipientName(message)}</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent specifically to admin */}
                       {message.senderType !== 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>👑</span>
                           <span className="font-semibold">Direct Message to Admin</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent by student specifically to admin */}
                       {message.senderType === 'student' && message.recipientType === 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>🎯</span>
                           <span className="font-semibold">Student → Admin Only</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent by employee specifically to admin */}
                       {message.senderType === 'employee' && message.recipientType === 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-blue-400 to-indigo-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>🎯</span>
                           <span className="font-semibold">Employee → Admin Only</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent by intern specifically to admin */}
                       {message.senderType === 'intern' && message.recipientType === 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>🎯</span>
                           <span className="font-semibold">Intern → Admin Only</span>
                         </div>
                       )}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatIcon; 